import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Fingerprint, Check } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PaymentForm from "@/components/PaymentForm";
import EmailVerificationStep from "@/components/EmailVerificationStep";


import { PLANS, PLAN_ORDER } from "@/lib/plans";

const PLAN_DESCS = {
  trial: "تجربة كاملة لجميع المزايا",
  basic: "الحضور والمساءلات والحسم بلا حدود",
  premium: "بوابة الموظفين والتعاميم والصلاحيات",
  semester: "كل الميزات + تقييم الأداء الوظيفي",
  year: "كل الميزات — أفضل قيمة للمدارس",
};

export default function Subscribe() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [plan, setPlan] = useState("trial");
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const p = searchParams.get("plan");
    if (PLAN_ORDER.includes(p)) setPlan(p);
  }, [searchParams]);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [verifyStep, setVerifyStep] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!/^05\d{8}$/.test(phone.trim())) {
      setError("رقم الجوال إلزامي بالصيغة السعودية 05xxxxxxxx (10 أرقام)");
      return;
    }
    setLoading(true);
    try {
      const u = await register(name, email, password, plan, phone.trim());
      if (u && u.email_verified === false) setVerifyStep(true);
      else if (plan === "trial") navigate("/");
      else setRegistered(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4" data-testid="subscribe-page">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-3 mb-8 justify-center">
          <div className="w-11 h-11 rounded-2xl bg-emerald-900 flex items-center justify-center">
            <Fingerprint className="w-6 h-6 text-amber-400" />
          </div>
          <div>
            <h1 className="text-xl font-extrabold text-emerald-950">الاشتراك في راصد حضوري</h1>
            <p className="text-xs text-slate-500">اختر الباقة وأنشئ حسابك خلال دقيقة</p>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5 mb-6">
          {PLAN_ORDER.map((id) => {
            const p = PLANS[id];
            return (
              <button key={id} type="button" onClick={() => setPlan(id)}
                data-testid={`plan-${id}`}
                className={`text-right rounded-2xl border-2 p-3.5 transition-all bg-white ${
                  plan === id ? "border-emerald-800 shadow-md" : "border-slate-200 hover:border-emerald-300"}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-bold text-xs">{p.name}</span>
                  {plan === id && <Check className="w-4 h-4 text-emerald-800 shrink-0" />}
                </div>
                <p className="text-lg font-extrabold text-emerald-900">{p.priceLabel}</p>
                <p className="text-[11px] text-amber-700 font-semibold">{p.duration}</p>
                <p className="text-[11px] text-slate-400 mt-1 leading-5">{PLAN_DESCS[id]}</p>
              </button>
            );
          })}
        </div>

        {plan !== "trial" && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4 mb-6" data-testid="payment-info-box">
            <p className="font-bold text-sm text-emerald-900 mb-1">طرق الدفع المتاحة</p>
            <p className="text-xs text-emerald-800 leading-6">
              بعد إنشاء الحساب أكمل الدفع بالبطاقة (مدى / فيزا / ماستركارد) عبر بوابة Moyasar الآمنة ويُفعَّل اشتراكك فوراً.
            </p>
          </div>
        )}

        {verifyStep ? (
          <EmailVerificationStep email={email}
            onVerified={() => { if (plan === "trial") navigate("/"); else { setVerifyStep(false); setRegistered(true); } }} />
        ) : registered ? (
          <div className="bg-white rounded-2xl border p-6 shadow-sm" data-testid="payment-step">
            <h2 className="font-bold text-emerald-950 mb-1">تم إنشاء حسابك بنجاح</h2>
            <p className="text-xs text-slate-500 mb-4 leading-6">
              أكمل الدفع بالبطاقة لإكمال تفعيل اشتراكك:
            </p>
            <PaymentForm plan={plan} />
          </div>
        ) : (
        <form onSubmit={submit} className="bg-white rounded-2xl border p-6 space-y-4 shadow-sm">
          <div className="space-y-1.5">
            <Label htmlFor="name">الاسم الكامل / اسم المدرسة</Label>
            <Input id="name" data-testid="subscribe-name-input" value={name}
              onChange={(e) => setName(e.target.value)} required placeholder="مثال: مدرسة النور الابتدائية" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="email">البريد الإلكتروني</Label>
            <Input id="email" type="email" dir="ltr" data-testid="subscribe-email-input" value={email}
              onChange={(e) => setEmail(e.target.value)} required placeholder="name@example.com" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="phone">رقم الجوال</Label>
            <Input id="phone" type="tel" dir="ltr" data-testid="subscribe-phone-input" value={phone}
              onChange={(e) => setPhone(e.target.value)} required placeholder="05xxxxxxxx"
              pattern="05[0-9]{8}" title="10 أرقام تبدأ بـ 05" maxLength={10} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="password">كلمة المرور</Label>
            <Input id="password" type="password" dir="ltr" data-testid="subscribe-password-input" value={password}
              onChange={(e) => setPassword(e.target.value)} required minLength={6} placeholder="6 أحرف على الأقل" />
          </div>
          {error && (
            <div data-testid="subscribe-error-alert"
              className="text-sm bg-rose-50 text-rose-700 border border-rose-200 rounded-lg px-3 py-2">
              {error}
            </div>
          )}
          <Button type="submit" data-testid="subscribe-submit-button" disabled={loading}
            className="w-full bg-emerald-900 hover:bg-emerald-950 text-white h-11">
            {loading ? "جارٍ إنشاء الحساب..." : plan === "trial" ? "ابدأ التجربة المجانية" : "إنشاء الحساب وإتمام الاشتراك"}
          </Button>
          <p className="text-center text-sm text-slate-500">
            لديك حساب؟{" "}
            <Link to="/login" data-testid="go-login-link" className="text-emerald-800 font-semibold hover:underline">
              تسجيل الدخول
            </Link>
          </p>
        </form>
        )}
      </div>
    </div>
  );
}
